import React, { useEffect, useState } from "react";
import {
  searchMenuItemImages,
  saveMenuItemImageFromUrl,
} from "../../services/menuApi";
import "./OnlineImageSearch.css";

const input = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #ddd",
  background: "#fff",
  fontSize: 14,
};
const btn = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #dfe3e6",
  background: "#fff",
  cursor: "pointer",
};
const btnPrimary = {
  ...btn,
  border: "1px solid rgba(255, 0, 0, 1)",
  background: "rgba(255, 0, 0, 1)",
  color: "#fff",
};
const smallBtn = { ...btn, padding: "6px 8px", fontSize: 12 };

const IMAGE_SEARCH_STOP = new Set([
  "a", "an", "the", "and", "or", "with", "of", "in", "on", "for", "to", "from",
  "this", "that", "our", "your", "is", "are", "was", "were", "be", "by", "at", "as",
  "into", "over", "under", "served", "topped", "made", "comes", "come",
  "including", "includes", "featuring", "delicious", "fresh", "house", "special",
  "style", "plus", "also", "then", "choice", "option", "options", "side", "sides",
]);

function normalizeMenuSearchText(s) {
  return String(s || "")
    .replace(/__/g, " ")
    .replace(/_/g, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function descriptionSearchKeywords(description, name, limit = 8) {
  const nameSet = new Set(
    normalizeMenuSearchText(name)
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
  );
  const unique = [];
  const seen = new Set();
  for (const w of normalizeMenuSearchText(description)
    .toLowerCase()
    .split(/[^a-z0-9]+/)) {
    if (!w || w.length < 3 || IMAGE_SEARCH_STOP.has(w) || nameSet.has(w) || seen.has(w)) {
      continue;
    }
    seen.add(w);
    unique.push(w);
    if (unique.length >= limit) break;
  }
  return unique.join(" ");
}

function buildImageSearchQuery(name, description) {
  const n = normalizeMenuSearchText(name);
  const extra = descriptionSearchKeywords(description, n);
  return [n, extra].filter(Boolean).join(" ").trim();
}

function filenameFromValue(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  try {
    const url = new URL(s, window.location.origin);
    const path = url.pathname || s;
    return path.split("/").filter(Boolean).pop() || s;
  } catch {
    return s.split("/").filter(Boolean).pop() || s;
  }
}

export default function OnlineImageSearch({
  itemName,
  itemDescription,
  existingFilename,
  tenantServerUrl,
  onApplied,
  isCategory = false,
  categoryCode = "",
  hint,
  placeholder,
}) {
  const initialQuery = buildImageSearchQuery(itemName, itemDescription);
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [err, setErr] = useState("");

  const runSearch = async (q) => {
    const term = String(q ?? query ?? "").trim();
    if (!term) {
      setErr("Enter a name to search.");
      return;
    }
    setSearching(true);
    setErr("");
    setResults([]);
    try {
      const res = await searchMenuItemImages(tenantServerUrl, {
        q: term,
        description: itemDescription,
        limit: 12,
      });
      const images = res?.images || [];
      setResults(images);
      if (!images.length) setErr("No matching images found. Try a simpler name.");
    } catch (ex) {
      setErr(ex?.message || "Image search failed.");
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (initialQuery) runSearch(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyImage = async (img) => {
    if (!img?.url || savingId) return;
    setSavingId(img.id || img.url);
    setErr("");
    try {
      const res = await saveMenuItemImageFromUrl(tenantServerUrl, {
        imageUrl: img.url,
        filename: existingFilename || "",
        item: itemName || query,
        isCategory: !!isCategory,
        code: categoryCode || "",
      });
      const filename =
        res?.filename ||
        filenameFromValue(res?.url || existingFilename || itemName);
      if (!filename) throw new Error("Server did not return a filename.");
      onApplied?.({
        filename,
        url:
          res?.url ||
          (isCategory
            ? `/admin/images/categories/${filename}`
            : `/admin/images/${filename}`),
        remoteUrl: img.url,
        thumb: img.thumb || img.url,
      });
    } catch (ex) {
      setErr(ex?.message || "Could not download and save that image.");
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="online-image-search">
      <form
        className="online-image-search-bar"
        onSubmit={(e) => {
          e.preventDefault();
          runSearch(query);
        }}
      >
        <input
          style={{ ...input, flex: 1 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder || "e.g. Grilled Salmon lemon garlic"}
          aria-label="Search for an image"
        />
        <button type="submit" style={smallBtn} disabled={searching || !query.trim()}>
          {searching ? "Searching…" : "Search"}
        </button>
        {results[0] && (
          <button
            type="button"
            style={btnPrimary}
            disabled={!!savingId}
            onClick={() => applyImage(results[0])}
            title="Download the top match and save it"
          >
            {savingId === (results[0].id || results[0].url)
              ? "Saving…"
              : "Use best match"}
          </button>
        )}
      </form>
      <div style={{ fontSize: 12, color: "#666", margin: "8px 0 10px" }}>
        {hint ||
          "Ranked photos using the name and description as keywords. Click one to download it and save it."}
      </div>
      {!!err && (
        <div style={{ color: "#b3261e", fontSize: 12, marginBottom: 8 }}>{err}</div>
      )}
      {searching && !results.length && (
        <div style={{ color: "#888", fontSize: 13, padding: "18px 0" }}>
          Looking up the most likely image…
        </div>
      )}
      <div className="online-image-search-grid">
        {results.map((img, i) => {
          const busy = savingId === (img.id || img.url);
          return (
            <button
              key={img.id || img.url || i}
              type="button"
              className={`online-image-search-card${i === 0 ? " is-best" : ""}`}
              onClick={() => applyImage(img)}
              disabled={!!savingId}
              title={img.title || "Use this image"}
            >
              <img
                src={img.thumb || img.url}
                alt={img.title || "Search result"}
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.currentTarget.style.opacity = "0.25";
                }}
              />
              <div className="online-image-search-meta">
                {i === 0 ? "Best match" : img.source || "Photo"}
                {busy ? " · Saving…" : ""}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
